import { useEffect, useMemo, useState } from 'react'
import { Button, Dialog, Input, Textarea } from '@/components/ui'
import { trpc } from '@/lib/trpc'
import { useAppStore } from '@/stores/app.store'

type Mode = 'full' | 'items' | 'flat'

interface Item {
  id: string
  kind: string
  numberLabel: string
  description: string
  quantity: number
  unit: string
}

/**
 * „Gutschrift erstellen" zu einer ausgestellten Rechnung: ganze Rechnung (Rest), einzelne Positionen
 * mit Menge oder ein Pauschalbetrag. Legt einen Entwurf an (Bezug zur Rechnung, Nummer GS-… erst beim
 * Ausstellen) und öffnet ihn zur Kontrolle. Die Rechnung selbst bleibt unverändert.
 */
export function CreditNoteDialog({
  open,
  onOpenChange,
  invoiceId,
  invoiceNumber,
  items,
  remaining,
  onCreated,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  invoiceId: string
  invoiceNumber: string
  items: Item[]
  /** je Position noch gutschreibbare Menge (fehlt eine Position → nichts mehr übrig) */
  remaining: Record<string, number>
  onCreated: (creditNoteId: string) => void
}) {
  const addToast = useAppStore((s) => s.addToast)
  const [mode, setMode] = useState<Mode>('full')
  const [reason, setReason] = useState('')
  const [picked, setPicked] = useState<Record<string, string>>({}) // itemId → Menge (Text, leer = nicht gewählt)
  const [flatDesc, setFlatDesc] = useState('')
  const [flatAmount, setFlatAmount] = useState('')

  useEffect(() => {
    if (open) {
      setMode('full')
      setReason('')
      setPicked({})
      setFlatDesc('')
      setFlatAmount('')
    }
  }, [open])

  const positions = useMemo(() => items.filter((i) => i.kind === 'position' && remaining[i.id] != null), [items, remaining])
  const nothingLeft = positions.length === 0

  const create = trpc.invoice.createCreditNote.useMutation({
    onSuccess: (gs) => {
      addToast({ type: 'success', title: 'Gutschrift-Entwurf angelegt', description: 'Bitte prüfen und ausstellen.' })
      onOpenChange(false)
      if (gs) onCreated(gs.id)
    },
    onError: (e) => addToast({ type: 'error', title: 'Gutschrift nicht möglich', description: e.message }),
  })

  const num = (s: string) => parseFloat(s.replace(',', '.'))
  const lines = positions
    .map((p) => ({ itemId: p.id, quantity: num(picked[p.id] ?? '') }))
    .filter((l) => Number.isFinite(l.quantity) && l.quantity > 0)
  const flatCents = Math.round(num(flatAmount) * 100)

  const canSubmit =
    !nothingLeft && (mode === 'full' || (mode === 'items' && lines.length > 0) || (mode === 'flat' && flatDesc.trim() && flatCents > 0))

  const submit = () =>
    create.mutate({
      invoiceId,
      mode,
      reason,
      lines: mode === 'items' ? lines : [],
      flat: mode === 'flat' ? { description: flatDesc.trim(), netCents: flatCents } : undefined,
    })

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      wide
      title="Gutschrift erstellen"
      description={`Zu Rechnung ${invoiceNumber} — die Rechnung selbst bleibt unverändert.`}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={submit} loading={create.isPending} disabled={!canSubmit}>
            Gutschrift-Entwurf anlegen
          </Button>
        </>
      }
    >
      {nothingLeft ? (
        <p className="text-sm text-text-secondary">
          Diese Rechnung ist bereits vollständig gutgeschrieben. Für einen zusätzlichen Preisnachlass ist eine Pauschal-Gutschrift möglich,
          solange der Nettobetrag der Rechnung nicht überschritten wird.
        </p>
      ) : null}

      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {(
            [
              ['full', 'Ganze Rechnung (Rest)'],
              ['items', 'Einzelne Positionen'],
              ['flat', 'Pauschaler Betrag'],
            ] as [Mode, string][]
          ).map(([m, label]) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`px-3 py-1.5 rounded-md text-sm border ${
                mode === m ? 'border-accent bg-accent/10 text-text-primary font-medium' : 'border-border text-text-secondary hover:bg-bg-hover'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {mode === 'full' && !nothingLeft && (
          <p className="text-sm text-text-secondary">
            Alle Positionen der Rechnung werden gutgeschrieben (bei bereits erfolgten Teilgutschriften nur der Rest). Für einen vollständigen Storno
            der Rechnung bitte „Stornieren" verwenden.
          </p>
        )}

        {mode === 'items' && (
          <div className="border border-border rounded-md divide-y divide-border-subtle max-h-72 overflow-y-auto">
            {positions.map((p) => (
              <div key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="w-10 text-text-tertiary">{p.numberLabel}</span>
                <span className="flex-1 min-w-0 truncate text-text-primary">{p.description}</span>
                <span className="text-xs text-text-tertiary whitespace-nowrap">
                  max. {String(remaining[p.id]).replace('.', ',')} {p.unit}
                </span>
                <input
                  className="bg-bg-input border border-border rounded px-2 py-1 w-24 text-right text-sm text-text-primary"
                  placeholder="Menge"
                  inputMode="decimal"
                  value={picked[p.id] ?? ''}
                  onChange={(e) => setPicked({ ...picked, [p.id]: e.target.value })}
                />
                <button
                  type="button"
                  className="text-xs text-accent hover:underline whitespace-nowrap"
                  onClick={() => setPicked({ ...picked, [p.id]: String(remaining[p.id]) })}
                >
                  alle
                </button>
              </div>
            ))}
          </div>
        )}

        {mode === 'flat' && (
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Input label="Bezeichnung" value={flatDesc} onChange={(e) => setFlatDesc(e.target.value)} placeholder="z. B. Preisnachlass Kulanz" />
            </div>
            <Input
              label="Betrag netto (€)"
              value={flatAmount}
              onChange={(e) => setFlatAmount(e.target.value)}
              placeholder="0,00"
              inputMode="decimal"
            />
            {flatCents > 0 && (
              <p className="col-span-3 text-xs text-text-tertiary">Steuersatz und Steuerart werden von der Rechnung übernommen.</p>
            )}
          </div>
        )}

        <Textarea
          label="Grund (erscheint auf der Gutschrift)"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="z. B. Falsche Menge geliefert"
        />
        {mode === 'items' && lines.length > 0 && (
          <p className="text-xs text-text-tertiary">
            {lines.length} Position{lines.length === 1 ? '' : 'en'} gewählt — der Betrag (inkl. anteiligem Rabatt und Steuer) steht im Entwurf.
          </p>
        )}
      </div>
    </Dialog>
  )
}
